import React from "react";
import { Link } from "react-router-dom";
import {
  Users, Sprout, MessageCircleHeart, HandHeart, ArrowRight,
  Home, Briefcase, HeartPulse, BookOpen,
} from "lucide-react";
import PageHero from "../components/PageHero";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";
import { SERVICES } from "../data/site";

const iconMap = { Users, Sprout, MessageCircleHeart, HandHeart };

const SIGNPOSTS = [
  { icon: HeartPulse, title: "NHS Mental Health", body: "Talking therapies, crisis teams and community mental health services." },
  { icon: Home, title: "Housing & Homelessness", body: "Shelter, local councils and rough sleeping outreach services." },
  { icon: Briefcase, title: "Employment Support", body: "Jobcentre Plus, training providers and back-to-work programmes." },
  { icon: BookOpen, title: "Domestic Abuse", body: "Refuge, Women's Aid and confidential local support." },
];

export default function Services() {
  useReveal();
  usePageMeta({ title: "Our Services", description: "Peer support, wellbeing workshops, one-to-one support and outreach — designed with community at the centre." });

  return (
    <>
      <PageHero
        eyebrow="Our services"
        title="Our services"
        lead="Peer support, wellbeing workshops, one-to-one sessions and outreach. All services are free and confidential."
      />

      <section className="container-page section-spacing">
        <div className="grid gap-6 md:grid-cols-2">
          {SERVICES.map((s) => {
            const Icon = iconMap[s.icon] || Users;
            return (
              <article
                key={s.slug}
                data-testid={`service-card-${s.slug}`}
                className="fade-up bg-white border border-[var(--tb-border)] rounded-3xl p-8 md:p-10 hover:-translate-y-1 hover:shadow-lg transition-all"
              >
                <span className="grid place-items-center h-14 w-14 rounded-2xl bg-[var(--tb-surface)] text-[var(--tb-primary)] mb-6">
                  <Icon className="h-6 w-6" />
                </span>
                <h2 className="font-serif-display text-2xl md:text-3xl text-[var(--tb-text)] mb-2">{s.title}</h2>
                <p className="text-[var(--tb-primary)] font-medium mb-4">{s.short}</p>
                <p className="text-[var(--tb-text-2)] text-pretty">{s.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="bg-[var(--tb-surface)] border-y border-[var(--tb-border)]">
        <div className="container-page section-spacing">
          <div className="max-w-3xl">
            <span className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">Signposting &amp; Referral</span>
            <h2 className="font-serif-display mt-3 text-3xl md:text-4xl lg:text-5xl text-[var(--tb-text)] text-balance">
              Signposting to other services
            </h2>
            <p className="mt-4 text-lg text-[var(--tb-text-2)] text-pretty">
              If you need support we cannot provide, we can refer or signpost you to the right service. We work
              alongside NHS, housing, employment and domestic abuse services.
            </p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {SIGNPOSTS.map((s) => (
              <article key={s.title} className="fade-up bg-white border border-[var(--tb-border)] rounded-2xl p-6">
                <s.icon className="h-7 w-7 text-[var(--tb-primary)] mb-3" />
                <h3 className="font-serif-display text-xl text-[var(--tb-text)] mb-2">{s.title}</h3>
                <p className="text-[var(--tb-text-2)] text-sm">{s.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="container-page section-spacing">
        <div className="rounded-3xl bg-[var(--tb-primary)] text-white p-8 md:p-14 grid gap-6 md:grid-cols-12 items-center">
          <div className="md:col-span-8">
            <h2 className="font-serif-display text-3xl md:text-4xl text-balance">
              Not sure which service is right for you?
            </h2>
            <p className="mt-3 text-white/90 text-lg max-w-2xl text-pretty">
              Get in touch and we&rsquo;ll help you find the right next step — with us, or with a partner service.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/get-support"
              data-testid="services-cta-button"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[var(--tb-primary)] hover:bg-[var(--tb-surface)] px-6 py-3.5 font-semibold"
            >
              Contact us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
